/**
 * The one place Studio binds a workspace-scoped rstore query to the runtime lifecycle (ADR 051).
 *
 * Every query is `cache-first`: hydration is the one fetch, later reads and `use()` never fetch, and
 * `refresh()` reloads through the same query so rows the response no longer contains leave `data`
 * (query tracking is on because `createWorkspaceRStore` sets `experimentalGarbageCollection`).
 */
import type { VueQueryFetchState } from '@rstore/vue';
import type { WorkspaceRuntimeManager } from '#layers/runtime/workspace/manager';
import type { WorkspaceRuntime, WorkspaceRuntimeStore } from '#layers/runtime/workspace/types';
import type { HydrationContext, WorkspaceHydration, WorkspaceHydrationReader } from './workspace-hydration';
import { computed } from 'vue';

/** The slice of an RStore query the workspace lifecycle owns. */
export type EntityQuery<TData = unknown> = PromiseLike<unknown> & {
	data: { value: TData };
	foreground: Pick<VueQueryFetchState, 'loading' | 'error'>;
	refresh: () => PromiseLike<unknown>;
};

export type EntityQueryData<TQuery extends EntityQuery> = TQuery['data']['value'];

/**
 * A `q.many` query resolves to an array; anything that is not an array (a `q.first` item or `null`)
 * is a singleton. The overloads of `defineWorkspaceEntityHydration` key `empty` on that split.
 */
export type SingletonEntityQueryData = Record<string, unknown> | null;

/** Options fixed when a workspace hydration is defined. */
interface WorkspaceHydrationDefinitionOptions<TQuery extends EntityQuery> {
	optional?: boolean;
	/** Override refresh behavior for queries that require explicit cache pruning. */
	refreshQuery?: (query: TQuery) => PromiseLike<unknown>;
}
const CACHE_FIRST = 'cache-first';

interface QueryBuilderOptions {
	fetchPolicy?: string;
}

interface CollectionQueryApi {
	query: (build: (builder: unknown) => QueryBuilderOptions) => unknown;
}

function isCollectionQueryApi(value: unknown): value is CollectionQueryApi {
	return typeof value === 'object' && value !== null && typeof (value as Partial<CollectionQueryApi>).query === 'function';
}

function pinCacheFirst(options: QueryBuilderOptions): QueryBuilderOptions {
	if (options.fetchPolicy !== undefined && options.fetchPolicy !== CACHE_FIRST) {
		throw new Error(`Workspace hydration queries are ${CACHE_FIRST}; the definition asked for "${options.fetchPolicy}"`);
	}

	return { ...options, fetchPolicy: CACHE_FIRST };
}

/** The store as the definition sees it: every collection's `query` pins the seam's fetch policy. */
function withCacheFirstQueries<TStore extends object>(store: TStore): TStore {
	return new Proxy(store, {
		get(target, key) {
			const value: unknown = Reflect.get(target, key);
			if (!isCollectionQueryApi(value)) return value;

			return Object.create(value, {
				query: { value: (build: (builder: unknown) => QueryBuilderOptions) => value.query((builder) => pinCacheFirst(build(builder))) },
			});
		},
	});
}

/** rstore parks a failed load on the query instead of rejecting; hydration and refresh must surface it. */
function throwQueryError(query: EntityQuery) {
	const error = query.foreground.error.value;
	if (error) throw error;
}

/**
 * Binds the definer to a manager so `TStore` types the store each query callback receives: the app
 * binds `workspaceRuntime` (see `hydration.ts`), a test binds a manager over its own store.
 */
export function createWorkspaceEntityHydrationDefiner<TStore extends WorkspaceRuntimeStore>(manager: WorkspaceRuntimeManager<TStore>) {
	type AnyRuntime = WorkspaceRuntime<WorkspaceRuntimeStore>;

	/**
	 * `empty` is what `use()` yields while no hydrated runtime is active; reads may run before any query
	 * exists, so the value is spelled at the definition and each overload checks it against the query's result.
	 */
	function defineWorkspaceEntityHydration<TQuery extends EntityQuery<readonly unknown[]>>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		options: { empty: [] } & WorkspaceHydrationDefinitionOptions<TQuery>,
	): WorkspaceHydration<EntityQueryData<TQuery>>;
	function defineWorkspaceEntityHydration<TQuery extends EntityQuery<SingletonEntityQueryData>>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		options: { empty: null } & WorkspaceHydrationDefinitionOptions<TQuery>,
	): WorkspaceHydration<EntityQueryData<TQuery>>;

	function defineWorkspaceEntityHydration<TQuery extends EntityQuery>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		{ empty, optional = false, refreshQuery }: { empty: [] | null } & WorkspaceHydrationDefinitionOptions<TQuery>,
	): WorkspaceHydration<EntityQueryData<TQuery>> {
		const queries = new WeakMap<AnyRuntime, TQuery>();

		function activeQuery() {
			const runtime = manager.activeRuntime.value;
			return runtime ? queries.get(runtime) : undefined;
		}

		async function hydrate(runtime: AnyRuntime, context: HydrationContext): Promise<EntityQueryData<TQuery>> {
			let query = queries.get(runtime);

			if (!query) {
				const created = runtime.scope.active ? runtime.scope.run(() => create(withCacheFirstQueries(runtime.store as TStore), context)) : undefined;
				if (!created) throw new Error(`Workspace runtime "${runtime.slug}" is disposed`);
				query = created;
				queries.set(runtime, query);
			}

			await query;
			// Optional hydration keeps preparation alive when this query is unavailable.
			if (!optional) throwQueryError(query);

			return (query.data.value ?? empty) as EntityQueryData<TQuery>;
		}

		async function refresh() {
			const query = activeQuery();
			if (!query) return;
			await (refreshQuery ? refreshQuery(query) : query.refresh());
			throwQueryError(query);
		}

		function use(): WorkspaceHydrationReader<EntityQueryData<TQuery>> {
			const data = computed(() => (activeQuery()?.data.value ?? empty) as EntityQueryData<TQuery>);
			const isRefreshing = computed(() => activeQuery()?.foreground.loading.value ?? false);

			return { data, isRefreshing, refresh };
		}

		return { hydrate, use };
	}

	return defineWorkspaceEntityHydration;
}
